import { useRef, useState, type FormEvent } from 'react';
import { Play } from 'lucide-react';

import { api, ApiError, asApiError } from '../lib/api';
import { runCommand } from '../lib/commands';
import { duration, moneyFine } from '../lib/format';
import { useBilling } from '../lib/session';
import type { CommandResult, Sandbox } from '../lib/types';
import { ErrorCallout, Lamp, Spinner } from './ui';

type StepState = 'active' | 'done' | 'fail';
interface Step {
  key: string;
  label: string;
  state: StepState;
  ms?: number;
}

const EXAMPLES = ['sw_vers', 'uname -m', 'sysctl -n machdep.cpu.brand_string', 'swift --version'];
const TRY_TIMEOUT_MS = 5 * 60_000;

/**
 * Runs one command on a real Mac from the browser: create a sandbox, run
 * the command, show what came back, delete the sandbox. Always deletes.
 */
export function TryIt({ usdPerHour }: { usdPerHour?: number }) {
  const [cmd, setCmd] = useState('sw_vers');
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [result, setResult] = useState<(CommandResult & { roundTripMs: number; cmd: string }) | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { reloadBilling } = useBilling();

  const upsert = (step: Step) => setSteps((prev) => [...prev.filter((s) => s.key !== step.key), step]);

  async function run(e?: FormEvent) {
    e?.preventDefault();
    const command = cmd.trim();
    if (!command || running) return;
    setRunning(true);
    setResult(null);
    setError(null);
    setSteps([{ key: 'create', label: 'Creating a sandbox', state: 'active' }]);
    let sandbox: Sandbox | null = null;
    try {
      const t0 = performance.now();
      sandbox = await api<Sandbox>('/v1/sandboxes', { method: 'POST', body: { session: 'headless', timeoutMs: TRY_TIMEOUT_MS } });
      upsert({ key: 'create', label: `Created ${sandbox.name}`, state: 'done', ms: performance.now() - t0 });
      upsert({ key: 'run', label: 'Running on a Mac', state: 'active' });
      const t1 = performance.now();
      // The plane answers within 50 s; a longer command comes back 202 and runCommand follows it.
      const answer = await runCommand(sandbox.id, { cmd: command, timeoutMs: 60_000 }, {
        onRunning: () => upsert({ key: 'run', label: 'Still running, following it', state: 'active' }),
      });
      const roundTripMs = performance.now() - t1;
      upsert({ key: 'run', label: `Ran ${command.length > 28 ? `${command.slice(0, 27)}…` : command}`, state: answer.exitCode === 0 ? 'done' : 'fail', ms: roundTripMs });
      setResult({ ...answer, roundTripMs, cmd: command });
    } catch (err) {
      const apiError = asApiError(err);
      setError(apiError);
      setSteps((prev) => prev.map((s) => (s.state === 'active' ? { ...s, state: 'fail', label: `${s.label}: ${apiError.code}` } : s)));
    } finally {
      if (sandbox) {
        const t2 = performance.now();
        upsert({ key: 'kill', label: 'Deleting the sandbox', state: 'active' });
        try {
          await api(`/v1/sandboxes/${sandbox.id}`, { method: 'DELETE' });
          upsert({ key: 'kill', label: 'Deleted the sandbox', state: 'done', ms: performance.now() - t2 });
        } catch (err) {
          upsert({ key: 'kill', label: `Delete failed: ${asApiError(err).code}`, state: 'fail' });
        }
      }
      setRunning(false);
      void reloadBilling();
    }
  }

  const reserve = usdPerHour ? (usdPerHour * TRY_TIMEOUT_MS) / 3_600_000 : null;

  return (
    <div className="stack" style={{ gap: 0 }}>
      <section className="panel" aria-labelledby="try-title">
        <div className="panel__bar">
          <span className="panel__title" id="try-title">
            Try it on a real Mac
          </span>
          <span className="panel__title" style={{ color: 'var(--panel-ink-3)' }}>
            headless · macOS
          </span>
        </div>
        <form className="term" onSubmit={run}>
          <div className="term__line">
            <span className="term__prompt" aria-hidden="true">
              $
            </span>
            <label htmlFor="try-cmd" className="visually-hidden">
              Command to run on a Mac
            </label>
            <input
              id="try-cmd"
              ref={inputRef}
              className="term__input"
              value={cmd}
              onChange={(e) => setCmd(e.target.value)}
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="off"
              disabled={running}
            />
            <button type="submit" className="btn btn--primary btn--sm" disabled={running || !cmd.trim()} aria-busy={running}>
              {running ? <Spinner size={13} /> : <Play aria-hidden="true" />}
              Run
            </button>
          </div>
          <div className="term__out" aria-live="polite">
            {steps.length === 0 ? (
              <>
                <p className="term__hint" style={{ margin: 0 }}>
                  Creates a sandbox, runs your command with zsh, prints what came back, then deletes the sandbox.
                </p>
                <div className="term__chips" aria-label="Examples">
                  {EXAMPLES.map((ex) => (
                    <button
                      key={ex}
                      type="button"
                      className="term__chip"
                      onClick={() => {
                        setCmd(ex);
                        inputRef.current?.focus();
                      }}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="stack" style={{ gap: 6 }}>
                {steps
                  .filter((s) => s.key !== 'kill')
                  .map((s) => (
                    <StepLine key={s.key} step={s} />
                  ))}
                {result ? (
                  <>
                    <pre className="term__stdout">
                      {result.stdout || (result.stderr ? '' : '(no output)')}
                      {result.stderr ? <span className="term__stderr">{result.stderr}</span> : null}
                    </pre>
                    <div className="term__meta">
                      <span>
                        exit <b className={result.exitCode === 0 ? undefined : 'bad'}>{result.exitCode}</b>
                      </span>
                      <span>
                        ran in <b>{duration(result.durationMs)}</b>
                      </span>
                      <span>
                        round trip <b>{duration(result.roundTripMs)}</b>
                      </span>
                      {result.timedOut ? <span className="bad">stopped at its 1 min limit</span> : null}
                      {result.truncated ? <span className="bad">output truncated to the last 1 MiB</span> : null}
                    </div>
                  </>
                ) : null}
                {steps
                  .filter((s) => s.key === 'kill')
                  .map((s) => (
                    <div key={s.key} style={{ marginTop: result ? 8 : 0 }}>
                      <StepLine step={s} />
                    </div>
                  ))}
              </div>
            )}
          </div>
        </form>
      </section>
      {error ? (
        <div style={{ marginTop: 12 }}>
          <ErrorCallout error={error} />
        </div>
      ) : null}
      <p className="try__caption">
        Uses your console session, not an API key.{reserve !== null ? ` Reserves ${moneyFine(reserve)} for its 5-minute timeout and bills only the seconds it runs.` : ''}
      </p>
    </div>
  );
}

function StepLine({ step }: { step: Step }) {
  return (
    <div className={`term__step is-${step.state}`}>
      {step.state === 'active' ? <Lamp tone="live" /> : <span className="lamp" aria-hidden="true" />}
      <span>{step.label}</span>
      {step.ms !== undefined ? <time>{duration(step.ms)}</time> : step.state === 'active' ? <time>…</time> : null}
    </div>
  );
}
